/**
 * A lazily-initialized task that supports cooperative cancellation.
 * The underlying factory is cancelled only when all callers who requested the value
 * have cancelled their requests.
 */
export class CooperativeCancellableLazy<T> {
    private readonly factory: (cancellationToken: AbortController) => Promise<T>;
    private readonly lock = {};

    private refCount = 0;
    private abortController: AbortController | null = null;
    private task: Promise<T> | null = null;

    constructor(factory: (cancellationToken: AbortController) => Promise<T>) {
        this.factory = factory;
    }

    async getValueAsync(callerToken: AbortSignal): Promise<T> {
        // Throw immediately if the caller's token is already cancelled
        if (callerToken.aborted) {
            throw new DOMException('The operation was aborted', 'AbortError');
        }

        // Increment reference count and create task if needed
        let taskToAwait: Promise<T>;
        
        // Critical section (simulated with synchronous execution)
        this.refCount++;

        // If this is the first caller, create the task
        if (this.task === null) {
            this.abortController = new AbortController();
            this.task = this.factory(this.abortController);
        }

        taskToAwait = this.task;

        try {
            // Create a promise that rejects when the caller's token is cancelled
            const cancellationPromise = new Promise<never>((_, reject) => {
                if (callerToken.aborted) {
                    reject(new DOMException('The operation was aborted', 'AbortError'));
                    return;
                }

                const onAbort = () => {
                    reject(new DOMException('The operation was aborted', 'AbortError'));
                };

                callerToken.addEventListener('abort', onAbort, { once: true });
            });

            // Wait for either the main task to complete or the caller to cancel
            const result = await Promise.race([
                taskToAwait,
                cancellationPromise
            ]);

            return result;
        } finally {
            // Critical section for cleanup
            this.refCount--;

            // If we are the last awaiter and the task failed, clean up so the next call starts fresh
            // But if the task succeeded, keep it cached for future use
            if (this.refCount === 0 && this.task !== null) {
                // Check if the task completed successfully or failed
                this.task.then(
                    // Success: Keep the task cached, don't clear it
                    () => {
                        // Task succeeded - keep it for future calls
                        // Don't clear this.task or this.abortController
                    },
                    // Failure: Clear the task so it can be retried
                    () => {
                        this.abortController?.abort();
                        this.abortController = null;
                        this.task = null;
                    }
                ).catch(() => {
                    // Additional safety: clear on any error
                    this.abortController?.abort();
                    this.abortController = null;
                    this.task = null;
                });
            }
        }
    }

    // Convenience method for cases where you don't need cancellation
    async getValue(): Promise<T> {
        const controller = new AbortController();
        return this.getValueAsync(controller.signal);
    }
}